/**
 * Simple Navigation Types
 * Basic types needed for the current app structure
 */

import { ComponentType } from 'react'

/**
 * Main application routes
 */
export enum AppRoutes {
  HOME = '/',
  FACILITATOR = '/facilitator',
  GAMER = '/gamer',
  FAQ = '/faq',
  EXPERT_VIDEOS = '/expert-videos',
  NOT_FOUND = '*'
}

/**
 * Route configuration
 */
export interface RouteConfig {
  path: AppRoutes
  element: ComponentType
  title?: string
}

/**
 * Enum for sidebar navigation sections
 * Used to define the available navigation items in the sidebar
 */
export enum SidebarSection {
  DASHBOARD_FACILITATOR = 'dashboard-facilitator',
  TRAINING_SPRINT_MANAGEMENT = 'training-sprint-management',
  SPRINT_CREATION = 'sprint-creation',
  DOWNLOAD_REPORTS = 'download-reports',
  SESSIONS = 'sessions',
  REPORTS = 'reports',
  LICENSES = 'licenses',
  GUIDES = 'guides',
  QA = 'qa',
  EXPERT_VIDEOS = 'expert-videos',
  PROFILE = 'profile',
  USERS = 'users',
  GAME_CENTER_FACILITATOR = 'game-center-facilitator',
  REFLECTIONS = 'reflections',
  ORGANIZATION_MEMBERS = 'organization-members',
  ORGANIZATION_INVITES = 'organization-invites',
  ORGANIZATION_SETTINGS = 'organization-settings'
}

/**
 * Enum for gamer role navigation sections
 * Used to define the available navigation items for gamers
 */
export enum GamerSection {
  DASHBOARD_GAMER = 'dashboard-gamer',
  MY_PEOPLE = 'my-people',
  LATEST_SESSION_FEEDBACK = 'latest-session-feedback',
  UPCOMING_ACTIVITIES = 'upcoming-activities',
  ALL_UPCOMING_ACTIVITIES = 'all-upcoming-activities',
  ONBOARDING = 'onboarding',
  ARIA_COACH = 'aria-coach',
  GAME_CENTER = 'game-center',
  MY_PROFILE = 'my-profile',
  FAQ = 'faq',
  ACCOUNT_SETTINGS = 'account-settings',
  ACTIVITY_DETAILS = 'activity-details',
  DEEPER_TEAM_ANALYSIS = 'deeper-team-analysis',
  DEEPER_INDIVIDUAL_ANALYSIS = 'deeper-individual-analysis',
  ACHIEVEMENTS = 'achievements'
}

/**
 * Enum for shared sections accessible by both gamer and facilitator
 * Used for game routes and dev tools
 */
export enum SharedSection {
  GAME_CENTER = 'game-center',
  PLAY_GAME_WITH_INTRODUCTION = 'play-game-with-introduction',
  PLAY_GAME_WITHOUT_INTRODUCTION = 'play-game-without-introduction',
  DEV_TOOLS = 'dev-tools',
  DEV_TOOLS_VOICE_LOTTIE = 'dev-tools-voice-lottie',
  DEV_TOOLS_BUBBLE_LOTTIE = 'dev-tools-bubble-lottie',
  DEV_TOOLS_VOICE_ANIMATION_AUDIO = 'dev-tools-voice-animation-audio',
  DEV_TOOLS_RECHARTS_GLASS = 'dev-tools-recharts-glass',
  DEV_TOOLS_COMMUNICATION_FEELING = 'dev-tools-communication-feeling',
  DEV_TOOLS_STACKED_CHARTS = 'dev-tools-stacked-charts',
  DEV_TOOLS_COMPARISON_BAR = 'dev-tools-comparison-bar',
  DEV_TOOLS_TEAM_HEALTH_HEART = 'dev-tools-team-health-heart',
  DEV_TOOLS_GLINT_REVEAL = 'dev-tools-glint-reveal',
  DEV_TOOLS_GENERATE_REPORT_VISUALS = 'dev-tools-generate-report-visuals',
  DEV_TOOLS_FUNNEL_GRAPH = 'dev-tools-funnel-graph',
  DEV_TOOLS_FUNNEL_GRAPH_V2 = 'dev-tools-funnel-graph-v2',
  DEV_TOOLS_FUNNEL_GRAPH_V3 = 'dev-tools-funnel-graph-v3',
  DEV_TOOLS_TEAM_INFORMATION_SHARES = 'dev-tools-team-information-shares',
  DEV_TOOLS_ACHIEVEMENTS = 'dev-tools-achievements',
  DEV_TOOLS_PERSONALITY_TRAITS = 'dev-tools-personality-traits',
  DEV_TOOLS_BO_CLEANUP = 'dev-tools-bo-cleanup'
}

/**
 * Union type for all possible sidebar sections
 * Allows sidebar to work with facilitator, gamer, and shared sections
 */
export type AnySidebarSection = SidebarSection | GamerSection | SharedSection

/**
 * Product wordmarks a sidebar entry can render instead of a plain translated label. Kept as an enum so
 * navigation stays plain data — the sidebar maps the value to the component.
 */
export enum SidebarBrand {
  ARIA_COACH = 'aria-coach'
}

/**
 * Interface for sidebar navigation items
 */
export interface SidebarNavigationItem {
  id: AnySidebarSection
  label: string
  icon: ComponentType<{ className?: string; style?: React.CSSProperties }>
  description?: string
  disabled?: boolean
  adminOnly?: boolean
  hideInSidebar?: boolean
  externalUrl?: string // If provided, opens in new tab instead of navigating internally
  /** Renders a branded wordmark in place of `label`; `label` stays as the accessible name. */
  brand?: SidebarBrand
}

/**
 * Interface for sidebar context state
 */
export interface SidebarContextState {
  activeSection: AnySidebarSection
  setActiveSection: (section: AnySidebarSection) => void
  navigationItems: SidebarNavigationItem[]
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
}
